/**
 * ColumnActionsMenu — per-row kebab menu for multi-type CSV import mapping.
 *
 * Two modes:
 * - Entity-type section rows (showRemove=true, default): "Remove" + "Move to {type}"
 * - Unassigned rows (showRemove=false): "Add to {type}" only
 */

import { Trash01 } from '@untitledui/icons';
import { memo, useCallback, useMemo } from 'react';

import { Dropdown } from '@/lib/base/overlays/dropdown';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons';

// ============================================================================
// Types
// ============================================================================

export interface MoveTarget {
  /** Entity type name (e.g. "ahoy_contact"). */
  entityTypeName: string;
  /** Human-readable label (e.g. "Contact"). */
  label: string;
  /** Virtual file key for the target section. */
  virtualKey: string;
}

interface ColumnActionsMenuProps {
  /** The CSV column this menu acts on. */
  column: string;
  /** Available entity types to move this column to. */
  moveTargets: MoveTarget[];
  /**
   * Called when the user picks "Remove" — moves column to unassigned.
   * Only used when `showRemove` is true.
   */
  onRemove?: (column: string) => void;
  /** Called when the user picks "Move to / Add to {type}". */
  onMoveTo: (column: string, targetVirtualKey: string) => void;
  /**
   * Whether to show the "Remove" action. Defaults to `true`.
   * Set to `false` for unassigned columns (already removed — show "Add to" instead).
   */
  showRemove?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const ColumnActionsMenu = memo(function ColumnActionsMenu({
  column,
  moveTargets,
  onRemove,
  onMoveTo,
  showRemove = true,
}: ColumnActionsMenuProps) {
  const handleAction = useCallback(
    (key: React.Key) => {
      if (key === 'remove') {
        onRemove?.(column);
        return;
      }
      // Move-to / Add-to actions are keyed by virtual key
      const target = moveTargets.find(t => t.virtualKey === String(key));
      if (target) {
        onMoveTo(column, target.virtualKey);
      }
    },
    [onRemove, moveTargets, column, onMoveTo]
  );

  const actionLabel = showRemove ? 'Move to' : 'Add to';

  // Build items for each entity type with its icon
  const moveItems = useMemo(
    () =>
      moveTargets.map(target => ({
        ...target,
        Icon: getEntityTypeIconComponent(target.entityTypeName),
      })),
    [moveTargets]
  );

  return (
    <Dropdown.Root>
      <Dropdown.DotsButton aria-label={`Actions for column ${column}`} />
      <Dropdown.Popover placement='bottom end'>
        <Dropdown.Menu onAction={handleAction}>
          {showRemove && (
            <Dropdown.Item id='remove' textValue='Remove' icon={Trash01}>
              Remove
            </Dropdown.Item>
          )}
          {showRemove && moveItems.length > 0 && <Dropdown.Separator />}
          {moveItems.map(item => (
            <Dropdown.Item
              key={item.virtualKey}
              id={item.virtualKey}
              textValue={`${actionLabel} ${item.label}`}
              icon={item.Icon}
            >
              {actionLabel} {item.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
});
